import { useTracking } from '@vp/react-tracking';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconProps, useScreenClass } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { COLLABORATE_BASE_PATH } from '../';
import { useCollaborationContext } from '../context';

export type NavigationLink = {
    icon: IconProps['iconType'];
    label: string;
    path?: string;
    onClick?: () => void;
    isActive?: boolean;
    openInNewTab?: boolean;
};

export function useNavigationLinks(): NavigationLink[] {
    const { trackingProperties, collaborationId } = useCollaborationContext();
    const { tracking } = useTracking();
    const screenClass = useScreenClass();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { deliveryVersionId } = useParams<{
        deliveryVersionId: string;
    }>();
    const deliveryVersionIdForRouting = deliveryVersionId || '1';

    const navigateToBrief = () => {
        const briefUrl = `${COLLABORATE_BASE_PATH}/${collaborationId}/delivery/${deliveryVersionIdForRouting}/brief`;

        tracking.track('Navigation Clicked', {
            ...trackingProperties,
            category: 'Collaboration Panel',
            label: 'Sidebar:Collaboration Panel',
            eventDetail: briefUrl,
            navigationDetail: 'Brief',
        });

        navigate(briefUrl);
    };

    const allParams = Object.fromEntries(searchParams.entries());
    const isMobile = screenClass === 'xs';
    const isDesignsPanelActive = searchParams.get('activePanel') === 'designs';

    // build up list of links
    const links: NavigationLink[] = [];

    links.push({
        icon: 'image',
        label: __('Brief'),
        onClick: navigateToBrief,
        isActive: location.pathname.includes('/brief') && !isDesignsPanelActive,
    });

    if (location.pathname.includes('/revision') && isMobile) {
        links.push({
            icon: 'edit',
            label: __('Revision request'),
            onClick: () => {
                navigate(
                    `${COLLABORATE_BASE_PATH}/${collaborationId}/delivery/${deliveryVersionIdForRouting}/revision`
                );
                tracking.track('Navigation Clicked', {
                    ...trackingProperties,
                    category: 'Collaboration Panel',
                    label: 'Sidebar:Collaboration Panel',
                    navigationDetail: 'Revision request',
                });
            },
            isActive: location.pathname.includes('/revision') && !isDesignsPanelActive,
        });
    } else {
        links.push({
            icon: 'liveChat',
            label: __('Activity'),
            onClick: () => {
                navigate(
                    `${COLLABORATE_BASE_PATH}/${collaborationId}/delivery/${deliveryVersionIdForRouting}/activity`
                );
                tracking.track('Navigation Clicked', {
                    ...trackingProperties,
                    category: 'Collaboration Panel',
                    label: 'Sidebar:Collaboration Panel',
                    navigationDetail: 'Activity',
                });
            },
            isActive: location.pathname.includes('/activity') && !isDesignsPanelActive,
        });
    }

    if (isMobile) {
        links.push({
            icon: 'image',
            label: __('Designs'),
            onClick: () => {
                setSearchParams({ ...allParams, activePanel: 'designs' });
                tracking.track('Navigation Clicked', {
                    ...trackingProperties,
                    category: 'Collaboration Panel',
                    label: 'Sidebar:Collaboration Panel',
                    navigationDetail: 'Designs',
                });
            },
            isActive: isDesignsPanelActive,
        });
    }

    return links;
}
